import { useEffect, useRef, useState } from "react";

/**
 * Renders a TOTP otpauth URI as a QR code via dynamic import of `qrcode`.
 * Falls back to the URI text if QR generation fails.
 */
export function TotpQr({ otpauthUri }: { otpauthUri: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    void (async () => {
      try {
        const QR = await import("qrcode");
        if (cancelled || !canvasRef.current) return;
        await QR.toCanvas(canvasRef.current, otpauthUri, {
          errorCorrectionLevel: "M",
          margin: 2,
          width: 200,
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [otpauthUri]);

  return (
    <div className="field">
      <label>Authenticator QR</label>
      {!failed ? (
        <canvas
          ref={canvasRef}
          aria-label="TOTP enrollment QR code"
          style={{ maxWidth: 200, width: "100%", height: "auto" }}
        />
      ) : null}
      <p className="subtitle" style={{ wordBreak: "break-all", fontSize: 12 }}>
        {otpauthUri}
      </p>
    </div>
  );
}

export function LiveDisabledDisclaimer() {
  return (
    <div className="login-disclaimer" role="note">
      Authenticated · Read-only · Simulation Mode · Live Execution Disabled. Session cookies are
      HttpOnly; credentials and MFA secrets are never stored in browser storage.
    </div>
  );
}

/** Recovery codes shown once after enrollment; caller must clear after acknowledge. */
export function RecoveryCodesOnce({
  codes,
  onContinue,
}: {
  codes: string[];
  onContinue: () => void;
}) {
  return (
    <>
      <div className="login-disclaimer" role="alert">
        Store these recovery codes offline now. They will not be shown again.
      </div>
      <ul
        style={{
          margin: 0,
          paddingLeft: 18,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <button type="button" className="btn btn-primary" onClick={onContinue}>
        I have saved these codes
      </button>
    </>
  );
}
